import { useEffect, useState } from "react";
import { api, type PublicAccount } from "../api.js";
import { useLocale } from "../ui.js";
import { safeErrorCode, safeErrorText } from "@smart-shikka/react";

function roleLabelKey(role: PublicAccount["role"]): "owner" | "subAdmin" | "member" {
  if (role === "super-admin") return "owner";
  if (role === "super-sub-admin") return "subAdmin";
  return "member";
}

export function AccountsPanel({ token }: { token: string }) {
  const { tr, locale } = useLocale();
  const [accounts, setAccounts] = useState<PublicAccount[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    void api.adminAccounts(token).then((res) => {
      setAccounts(res.ok && res.data ? res.data.accounts : []);
    });
  }
  useEffect(load, [token]);

  const hasSubAdmin = (accounts ?? []).some((a) => a.role === "super-sub-admin");

  async function setRole(email: string, role: "user" | "super-sub-admin") {
    setBusy(email);
    setError(null);
    const res = await api.adminSetRole(token, email, role);
    setBusy(null);
    if (res.ok) load();
    else setError(safeErrorCode(res.denialCode ?? res.error ?? "generic_error"));
  }

  return (
    <section className="panel-card">
      <h2>{tr("accounts")}</h2>
      <p className="section-desc">{tr("owner")} · {tr("role")}</p>
      {error ? <div className="msg error">{safeErrorText(error, locale === "hi" ? "hi" : "en").title} <code>{error}</code></div> : null}
      {accounts === null ? (
        <p className="muted">{tr("loading")}</p>
      ) : accounts.length === 0 ? (
        <p className="muted">{tr("emptyList")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{tr("email")}</th>
              <th>{tr("role")}</th>
              <th>{tr("status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.email}>
                <td>{a.email}</td>
                <td>{tr(roleLabelKey(a.role))}</td>
                <td>
                  <span className={`pill ${a.status}`}>{a.status}</span>
                </td>
                <td>
                  {a.role === "super-sub-admin" ? (
                    <button
                      className="ss-btn-ghost"
                      disabled={busy === a.email}
                      onClick={() => void setRole(a.email, "user")}
                    >
                      {tr("removeSubAdmin")}
                    </button>
                  ) : a.role === "user" ? (
                    <button
                      className="ss-btn-ghost"
                      disabled={busy === a.email || hasSubAdmin || a.status !== "active"}
                      title={hasSubAdmin ? tr("subAdmin") : undefined}
                      onClick={() => void setRole(a.email, "super-sub-admin")}
                    >
                      {tr("makeSubAdmin")}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
